import { FC } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useDisclosure, VStack } from '@chakra-ui/react';

import { useCompanyBankAccounts } from 'common/hooks/useCompanyBankAccounts';
import { useInternationalTransferFeature } from 'common/hooks/useInternationalTransferFeature';
import { useMultiWalletFeature } from 'common/hooks/useMultiWalletFeature';
import SunshineCard from 'components/_core/SunshineCard';
import CardTitle from 'components/_core/SunshineCard/components/Title';
import SquircleIcon from 'components/_core/SunshineSquircleIcon';
import ErrorView from 'components/ErrorView';
import Loader from 'components/Loader';
import MultiWalletUpsellModal from 'components/MultiWalletUpsellModal';
import NewBadge from 'components/NewBadge';
import * as bankAccountRoutes from 'features/Bank/routes';
import * as ContentLayout from 'layouts/ContentLayout';

import NavigationHeader from '../../components/NavigationHeader';
import { navigateToBankTransfer } from '../../helpers';
import routes from '../../routes';
import useTransfersHome from './hooks/useTransfersHome';
import locales from './locales';

const TransfersHome: FC = () => {
  const history = useHistory();
  const { isMultiWalletFeatureAvailableForCompanyPlan } =
    useMultiWalletFeature();
  const { data, error, loading } = useTransfersHome();
  const {
    isOpen: isUpgradeModalOpen,
    onClose: onUpgradeModalClose,
    onOpen: onUpgradeModalOpen,
  } = useDisclosure();
  const {
    bankAccounts,
    error: bankAccountsError,
    loading: loadingBankAccounts,
  } = useCompanyBankAccounts();
  const { canAccessInternationalTransfer } = useInternationalTransferFeature();

  const handleNewInternalTransferClick = () => {
    if (!isMultiWalletFeatureAvailableForCompanyPlan) {
      onUpgradeModalOpen();
      return;
    }

    if (bankAccounts.length < 2) {
      history.push(bankAccountRoutes.newBankAccount);
      return;
    }

    navigateToBankTransfer(history, {
      bankTransfer: {
        transferType: 'internal',
      },
      variant: 'default',
    });
  };

  const handleNewExternalTransferClick = () => {
    navigateToBankTransfer(history, {
      bankTransfer: {
        transferType: 'external',
      },
      variant: 'default',
    });
  };

  const handleNewInternationalTransferClick = () => {
    navigateToBankTransfer(history, {
      bankTransfer: {
        transferType: 'international',
      },
      variant: 'default',
    });
  };

  if (error || bankAccountsError) {
    return <ErrorView />;
  }

  if (loading || !data || loadingBankAccounts || !bankAccounts) {
    return <Loader />;
  }

  return (
    <ContentLayout.Root>
      <ContentLayout.Body isScrollable={false}>
        <NavigationHeader />
        <ContentLayout.Header title={locales.title} />
        <ContentLayout.BodyScrollableSection paddingTop={0}>
          <VStack align="stretch" gap="space-32">
            <div>
              <SunshineCard.Group>
                <SunshineCard
                  onClick={handleNewExternalTransferClick}
                  title={locales.oneOffSepaTransfer}
                >
                  <SunshineCard.Slot name="aside">
                    <SquircleIcon borderRadius="radius-12" name="euro" />
                  </SunshineCard.Slot>
                  <SunshineCard.Text>
                    {locales.oneOffSepaTransferDescription}
                  </SunshineCard.Text>
                  <SunshineCard.Slot name="action">
                    <SunshineCard.SunshineIcon name="chevron-right" />
                  </SunshineCard.Slot>
                </SunshineCard>
                {canAccessInternationalTransfer ? (
                  <SunshineCard onClick={handleNewInternationalTransferClick}>
                    <CardTitle display="flex" gap={8}>
                      {locales.international}
                      <NewBadge />
                    </CardTitle>
                    <SunshineCard.Slot name="aside">
                      <SquircleIcon borderRadius="radius-12" name="earth" />
                    </SunshineCard.Slot>
                    <SunshineCard.Text>
                      {locales.internationalTransferDescription}
                    </SunshineCard.Text>
                    <SunshineCard.Slot name="action">
                      <SunshineCard.SunshineIcon name="chevron-right" />
                    </SunshineCard.Slot>
                  </SunshineCard>
                ) : null}
                <SunshineCard
                  as={Link}
                  title={locales.bulkTransfer}
                  to={
                    data.hasAccessToBulkTransfers
                      ? routes.bulkTransfers
                      : '/upgrade/feature-preview/bulkTransfers'
                  }
                >
                  <SunshineCard.Slot name="aside">
                    <SquircleIcon
                      borderRadius="radius-12"
                      name="bulk-transfer"
                    />
                  </SunshineCard.Slot>
                  <SunshineCard.Text>
                    {data.hasAccessToBulkTransfers
                      ? locales.bulkTransferDescription
                      : locales.formatString(locales.availableInHigherPlan, {
                          planName:
                            data.higherPlanWithBulkTransfers?.brandName ?? '',
                        })}
                  </SunshineCard.Text>
                  <SunshineCard.Slot name="action">
                    <SunshineCard.SunshineIcon name="chevron-right" />
                  </SunshineCard.Slot>
                </SunshineCard>
              </SunshineCard.Group>
            </div>
            <div>
              <SunshineCard.Group>
                <SunshineCard
                  onClick={handleNewInternalTransferClick}
                  title={locales.walletToWallet}
                >
                  <SunshineCard.Slot name="aside">
                    <SquircleIcon borderRadius="radius-12" name="payment" />
                  </SunshineCard.Slot>
                  <SunshineCard.Text>
                    {locales.walletToWalletDescription}
                  </SunshineCard.Text>
                  <SunshineCard.Slot name="action">
                    <SunshineCard.SunshineIcon name="chevron-right" />
                  </SunshineCard.Slot>
                </SunshineCard>
              </SunshineCard.Group>
            </div>
          </VStack>
          {isUpgradeModalOpen ? (
            <MultiWalletUpsellModal
              isOpen={isUpgradeModalOpen}
              onClose={onUpgradeModalClose}
            />
          ) : null}
        </ContentLayout.BodyScrollableSection>
      </ContentLayout.Body>
    </ContentLayout.Root>
  );
};

export default TransfersHome;
